/**
 * Web component for Anear dropdowns with optional auto-submit.
 *
 * Usage:
 *   <anear-drop-down
 *     name="suggestedTemplateId"
 *     items='[{"value":"a","label":"Option A","tags":["tag"]}]'
 *     placeholder="Select..."
 *     submit-mode="auto"
 *     submit-name="editorAction"
 *     submit-value="template"
 *     submit-on-empty="false"
 *   ></anear-drop-down>
 *
 * The selected value is stored in a hidden input for form integration.
 */
class AnearDropDown extends HTMLElement {
  constructor() {
    super()
    this._hiddenInput = null
    this._selectEl = null
    this._wrapper = null
  }

  static get observedAttributes() {
    return ['name', 'items', 'value', 'placeholder']
  }

  connectedCallback() {
    this.render()
  }

  attributeChangedCallback() {
    this.render()
  }

  get fieldName() {
    return this.getAttribute('name') || 'dropdownValue'
  }

  get value() {
    return this.getAttribute('value') || ''
  }

  get placeholder() {
    return this.getAttribute('placeholder') || 'Select an option...'
  }

  get submitOnChange() {
    const raw = this.getAttribute('submit-on-change')
    if (raw === null) return false
    return raw !== 'false'
  }

  get submitMode() {
    const raw = (this.getAttribute('submit-mode') || '').toLowerCase().trim()
    if (raw === 'auto' || raw === 'manual') return raw
    // Backward compatibility with older submit-on-change attribute.
    return this.submitOnChange ? 'auto' : 'manual'
  }

  get submitOnEmpty() {
    const raw = this.getAttribute('submit-on-empty')
    if (raw === null) return false
    return raw !== 'false'
  }

  get submitName() {
    return this.getAttribute('submit-name') || 'submit'
  }

  get submitValue() {
    return this.getAttribute('submit-value') || 'select'
  }

  parseItems() {
    const raw = this.getAttribute('items') || '[]'
    const decodeHtmlEntities = (value) => value
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch (_e) {
      try {
        const decoded = decodeURIComponent(raw)
        const parsedDecoded = JSON.parse(decoded)
        return Array.isArray(parsedDecoded) ? parsedDecoded : []
      } catch (_e2) {
        try {
          const entityDecoded = decodeHtmlEntities(raw)
          const parsedEntityDecoded = JSON.parse(entityDecoded)
          return Array.isArray(parsedEntityDecoded) ? parsedEntityDecoded : []
        } catch (_e3) {
          return []
        }
      }
    }
  }

  submitClosestForm() {
    if (this.submitMode !== 'auto') return
    if (!this.submitOnEmpty && !(this._hiddenInput?.value || '').trim()) return
    const form = this.closest('anear-form')
    if (!form) return

    const submitter = {
      name: this.submitName,
      value: this.submitValue,
      getAttribute: (attr) => {
        if (attr === 'name') return this.submitName
        if (attr === 'value') return this.submitValue
        return null
      }
    }

    const submitEvent = new Event('submit', {
      bubbles: true,
      cancelable: true
    })
    Object.defineProperty(submitEvent, 'submitter', {
      value: submitter,
      writable: false,
      configurable: false
    })
    form.dispatchEvent(submitEvent)
  }

  createOptionText(item) {
    const label = typeof item?.label === 'string' ? item.label : ''
    const tags = Array.isArray(item?.tags) ? item.tags.filter(tag => typeof tag === 'string' && tag.trim()) : []
    if (!tags.length) return label
    return `${label} (${tags.join(', ')})`
  }

  render() {
    const items = this.parseItems()
    const selectedValue = this.value

    if (!this._wrapper) {
      this._wrapper = document.createElement('div')
      this._wrapper.className = 'anear-drop-down'

      this._hiddenInput = document.createElement('input')
      this._hiddenInput.type = 'hidden'

      this._selectEl = document.createElement('select')
      this._selectEl.className = 'input-field anear-drop-down-select'
      this._selectEl.addEventListener('change', () => {
        const nextValue = this._selectEl?.value || ''
        if (this._hiddenInput) this._hiddenInput.value = nextValue
        this.setAttribute('value', nextValue)
        this.dispatchEvent(new CustomEvent('change', {
          bubbles: true,
          detail: { value: nextValue }
        }))
        this.submitClosestForm()
      })

      this._wrapper.appendChild(this._hiddenInput)
      this._wrapper.appendChild(this._selectEl)
      this.appendChild(this._wrapper)
    }

    this._hiddenInput.name = this.fieldName
    this._hiddenInput.value = selectedValue
    this._selectEl.name = this.fieldName

    this._selectEl.innerHTML = ''

    const placeholderOpt = document.createElement('option')
    placeholderOpt.value = ''
    placeholderOpt.textContent = this.placeholder
    this._selectEl.appendChild(placeholderOpt)

    items.forEach((item) => {
      const option = document.createElement('option')
      option.value = String(item?.value || '')
      option.textContent = this.createOptionText(item)
      this._selectEl.appendChild(option)
    })

    this._selectEl.value = selectedValue
  }
}

if (!customElements.get('anear-drop-down')) {
  customElements.define('anear-drop-down', AnearDropDown)
}

export default AnearDropDown
